import { Routes } from '@angular/router';
import { AccountLayoutComponent, AdminLayoutSidebarLargeComponent, AuthGuard, AuthLayoutComponent, BlankLayoutComponent } from '@kiraah-shared/app-skeleton';
import { LvfAppModule, LvfMissionType, LvfTrainingSessionPackageType } from '@kiraah-shared/common';
import { ALLOWED_MISSION_TYPES, TASK_ROUTE_CONTEXT, createMemoizedComponentLoader } from '@kiraah-shared/core';
import { RedirectComponent } from './redirect.component';

const loadAuditDuerRiskPicker = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/consulting/audit').then(m => m.AuditDuerRiskPickerComponent)
);

// Routes communes à toutes les applications
const commonAdminRoutes: Routes = [
  {
    path: 'home',
    loadChildren: () => import('@kiraah-shared/consulting/application-wrapper').then(m => m.DefaultAppWrapperRoutingModule)
  },
  {
    path: 'default',
    loadChildren: () => import('@kiraah-shared/consulting/application-wrapper').then(m => m.DefaultAppWrapperRoutingModule)
  },
  {
    path: 'agency',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.AgencyRoutingModule)
  },
  {
    path: 'businessunit',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.BusinessUnitRoutingModule)
  },
  {
    path: 'activity',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.ActivityRoutingModule)
  },
  {
    path: 'application',
    loadChildren: () => import('@kiraah-shared/app-skeleton').then(m => m.ApplicationRoutingModule)
  },
  {
    path: 'locality',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.LocalityRoutingModule)
  },
  {
    path: 'faq',
    loadChildren: () => import('@kiraah-shared/app-skeleton').then(m => m.FaqRoutingModule)
  },
  // {
  //   path: 'dashboard',
  //   loadChildren: () => import('@kiraah-shared/daily-activity').then(m => m.DashboardRequestRoutingModule)
  // },
  // {
  //   path: 'dashboard',
  //   loadChildren: () => import('@kiraah-shared/consulting').then(m => m.DashboardRoutingModule)
  // },
  {
    path: 'dashboard',
    loadChildren: () => import('@kiraah-shared/consulting/dashboard').then(m => m.DashboardRoutingModule)
  },
  {
    path: 'document/compliance',
    loadComponent: () => import('@kiraah-shared/daily-activity/document').then(m => m.DocumentComplianceRouteComponent)
  },
  {
    path: 'planning/calendar',
    loadComponent: () => import('@kiraah-shared/consulting/application-wrapper').then(m => m.DefaultPlanningComponent)
  },
  {
    path: 'document-type',
    loadChildren: () => import('@kiraah-shared/daily-activity/document').then(m => m.DocumentTypeRoutingModule)
  },
  {
    path: 'document-size-limit',
    loadChildren: () => import('@kiraah-shared/daily-activity/document').then(m => m.DocumentSizeLimitRoutingModule)
  },
  {
    path: 'entity-status/list',
    loadComponent: () => import('@kiraah-shared/app-skeleton').then(m => m.EntityStatusWorkflowListComponent)
  },
  {
    path: 'team',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.TeamRoutingModule)
  },
  {
    path: 'identity',
    loadChildren: () => import('@kiraah-shared/identity').then(m => m.IdentityRoutingModule)
  },
  {
    path: 'thirdpart',
    loadChildren: () => import('@kiraah-shared/thirdpart').then(m => m.ThirdPartRoutingModule)
  },
  {
    path: 'profil',
    loadChildren: () => import('@kiraah-shared/entity').then(m => m.ProfilRoutingModule)
  },
  {
    path: 'task',
    loadChildren: () => import('@kiraah-shared/daily-activity/task').then(m => m.TaskRoutingModule)
  },
  {
    path: 'task/audit-duer',
    loadChildren: () => import('@kiraah-shared/daily-activity/task').then(m => m.TaskRoutingModule),
    providers: [
      {
        provide: TASK_ROUTE_CONTEXT,
        useValue: {
          scope: 'mission-audit-duer',
          baseTaskListPath: '/task/audit-duer/list',
          allowedMissionTypes: [LvfMissionType.AUDIT, LvfMissionType.CONSULTING],
          enableDuerFeatures: true,
          showBatchRiskLinkButton: true,
          batchRiskPickerComponentLoader: loadAuditDuerRiskPicker
        }
      }
    ]
  },
  {
    path: 'task/btp',
    loadChildren: () => import('@kiraah-shared/daily-activity/task').then(m => m.TaskRoutingModule),
    providers: [
      {
        provide: TASK_ROUTE_CONTEXT,
        useValue: {
          scope: 'mission-btp',
          baseTaskListPath: '/task/btp/list',
          allowedMissionTypes: [LvfMissionType.BTP]
        }
      }
    ]
  },
  {
    path: 'task/training-session',
    loadChildren: () => import('@kiraah-shared/daily-activity/task').then(m => m.TaskRoutingModule),
    providers: [
      {
        provide: TASK_ROUTE_CONTEXT,
        useValue: {
          scope: 'training-session',
          baseTaskListPath: '/task/training-session/list',
          allowedTrainingPackages: [
            LvfTrainingSessionPackageType.STANDARD,
            LvfTrainingSessionPackageType.SURVEY_SIGNING
          ],
          taskAssociationModule: LvfAppModule.ELEARNING_SESSION_MANAGEMENT
        }
      }
    ]
  },
  {
    path: 'feature-requests',
    loadChildren: () => import('@kiraah-shared/daily-activity/feature-request').then(m => m.FeatureRequestRoutingModule)
  },
  {
    path: 'template',
    loadChildren: () => import('@kiraah-shared/daily-activity/template-builder').then(m => m.TemplateBuilderRoutingModule)
  },
  {
    path: 'annotation',
    loadChildren: () => import('@kiraah-shared/daily-activity/annotation').then(m => m.AnnotationRoutingModule)
  },
  {
    path: 'e-signature',
    loadChildren: () => import('@kiraah-shared/daily-activity/signature').then(m => m.ESignatureRoutingModule)
  },
  {
    path: 'incident',
    loadChildren: () => import('@kiraah-shared/daily-activity/incident').then(m => m.IncidentRoutingModule)
  },
  {
    path: 'activity-summary',
    loadChildren: () => import('@kiraah-shared/daily-activity/activity-summary').then(m => m.ActivitySummaryRoutingModule)
  },
  {
    path: 'holiday',
    loadChildren: () => import('@kiraah-shared/daily-activity/holiday').then(m => m.HolidayRoutingModule)
  },
  {
    path: 'export-config',
    loadChildren: () => import('@kiraah-shared/daily-activity/administration').then(m => m.ExportConfigRoutingModule)
  },
  {
    path: 'status-reason',
    loadChildren: () => import('@kiraah-shared/daily-activity/status-reason').then(m => m.StatusReasonTypeRoutingModule)
  },
  {
    path: 'equipment',
    loadChildren: () => import('@kiraah-shared/supplychain').then(m => m.equipmentRoutes)
  },
  // Legacy vehicle-first URLs kept as short-lived redirects.
  { path: 'vehicle/equipment-list', redirectTo: 'equipment/equipment-list', pathMatch: 'full' },
  { path: 'vehicle/list', redirectTo: 'equipment/equipment-list', pathMatch: 'full' },
  { path: 'vehicle/brand/list', redirectTo: 'equipment/brand/list', pathMatch: 'full' },
  { path: 'vehicle/assignment/list', redirectTo: 'equipment/assignment/list', pathMatch: 'full' },
  { path: 'vehicle/maintenance/list', redirectTo: 'equipment/maintenance/list', pathMatch: 'full' },
  { path: 'vehicle/traffic-ticket/list', redirectTo: 'equipment/traffic-ticket/list', pathMatch: 'full' },
  { path: 'vehicle/kilometer/list', redirectTo: 'equipment/kilometer/list', pathMatch: 'full' },
  { path: 'vehicle/incident/list', redirectTo: 'equipment/incident/list', pathMatch: 'full' },
  { path: 'vehicle/inventory/list', redirectTo: 'equipment/inventory/list', pathMatch: 'full' },
  { path: 'vehicle/type/list', redirectTo: 'equipment/type/list', pathMatch: 'full' },
  { path: 'vehicle/new', redirectTo: 'equipment/new', pathMatch: 'full' },
  { path: 'vehicle', redirectTo: 'equipment', pathMatch: 'full' },
  {
    path: 'elearning',
    loadChildren: () => import('@kiraah-shared/academy').then(m => m.ElearningRoutingModule)
  },
  {
    path: 'veille',
    loadChildren: () => import('@kiraah-shared/academy').then(m => m.VeilleRoutingModule)
  },
  {
    path: 'stock',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.StockRoutingModule)
  },
  {
    path: 'budget-line',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.BudgetLineRoutingModule)
  },
  {
    path: 'purchase-request',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.PurchaseRequestRoutingModule)
  },
  {
    path: 'supplier-quotation-submission',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.SupplierQuotationSubmissionRoutingModule)
  },
  {
    path: 'invoice',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.InvoiceRoutingModule)
  },
  {
    path: 'product',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.ProductRoutingModule)
  },
  {
    path: 'unit',
    loadChildren: () => import('@kiraah-shared/organization').then(m => m.UnitRoutingModule)
  },
  {
    path: 'quotation',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.ProductQuotationRoutingModule)
  },
  {
    path: 'order',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.ProductOrderRoutingModule)
  },
  {
    path: 'delivery-order',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.DeliveryOrderRoutingModule)
  },
  {
    path: 'delivery-note',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.DeliveryNoteRoutingModule)
  },
  {
    path: 'opportunity',
    loadChildren: () => import('@kiraah-shared/daily-activity/opportunity').then(m => m.OpportunityRoutingModule)
  },
  {
    path: 'delivery-order',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.DeliveryOrderPlanningRoutingModule)
  },
  {
    path: 'ticket',
    loadChildren: () => import('@kiraah-shared/sales').then(m => m.TicketRoutingModule)
  },
  {
    path: 'accreditation',
    loadChildren: () => import('@kiraah-shared/consulting/accreditation').then(m => m.AccreditationRoutingModule)
  },
  {
    path: 'project',
    loadChildren: () => import('@kiraah-shared/consulting/project').then(m => m.ProjectRoutingModule)
  },
  {
    path: 'mission/audit-consulting',
    loadChildren: () => import('@kiraah-shared/consulting/project').then(m => m.ProjectRoutingModule),
    providers: [
      {
        provide: ALLOWED_MISSION_TYPES,
        useValue: [LvfMissionType.AUDIT, LvfMissionType.CONSULTING]
      }
    ]
  },
  {
    path: 'mission/btp',
    loadChildren: () => import('@kiraah-shared/consulting/project').then(m => m.ProjectRoutingModule),
    providers: [
      {
        provide: ALLOWED_MISSION_TYPES,
        useValue: [LvfMissionType.BTP]
      }
    ]
  },
  {
    path: 'audit',
    loadChildren: () => import('@kiraah-shared/consulting/audit').then(m => m.AuditRoutingModule)
  },
  {
    path: 'mission-type',
    loadChildren: () => import('@kiraah-shared/consulting/mission-type').then(m => m.MissionTypeRoutingModule)
  },
  {
    path: 'construction-site',
    loadChildren: () => import('@kiraah-shared/consulting/construction-site').then(m => m.ConstructionSiteRoutingModule)
  },
  {
    path: 'mission',
    loadChildren: () => import('@kiraah-shared/consulting/mission').then(m => m.MissionRoutingModule)
  },
  {
    path: 'booking',
    loadChildren: () => import('@kiraah-shared/consulting/booking').then(m => m.BookingRoutingModule)
  },
  {
    path: 'licensing',
    loadChildren: () => import('@kiraah-shared/licensing').then(m => m.TenantLicensingRoutingModule)
  },
  {
    path: 'backoffice/licensing',
    loadChildren: () => import('@kiraah-shared/licensing').then(m => m.BackofficeRoutingModule)
  },
  {
    path: 'backoffice/veille',
    loadChildren: () => import('@kiraah-shared/licensing').then(m => m.VeilleBackofficeRoutingModule)
  },
  {
    path: 'tenant',
    loadChildren: () => import('@kiraah-shared/licensing').then(m => m.TenantAdminRoutingModule)
  },
  {
    path: 'site-api-key',
    redirectTo: '/backoffice/licensing/site-api-keys/list',
    pathMatch: 'prefix',
  },
  {
    path: 'notification-config',
    loadChildren: () => import('@kiraah-shared/consulting/notification-config').then(m => m.NotificationConfigRoutingModule)
  }
];

// Routes principales
export const routes: Routes = [
  {
    path: '',
    component: RedirectComponent,
    pathMatch: 'full'
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      // Route pour les applications du saas
      {
        path: 'sessions',
        loadChildren: () => import('@kiraah-shared/app-skeleton').then(m => m.DefaultSessionsRoutingModule)
      }
    ]
  },
  {
    path: 'account',
    component: AccountLayoutComponent,
    loadChildren: () => import('@kiraah-shared/app-skeleton').then(m => m.AccountRoutingModule)
  },
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      {
        path: 'e-signature/gateway/:token',
        loadComponent: () => import('@kiraah-shared/daily-activity/signature').then(m => m.ESignatureGatewayComponent)
      },
      {
        path: 'e-signature/public/:token',
        loadComponent: () => import('@kiraah-shared/daily-activity/signature').then(m => m.ESignatureValidatorTabComponent)
      },
      {
        path: 'presence/gateway/:token',
        loadComponent: () => import('@kiraah-shared/identity').then(m => m.PresenceGatewayComponent)
      },
      {
        path: 'presence/sign/:token',
        loadComponent: () => import('@kiraah-shared/identity').then(m => m.PresencePublicSignPageComponent)
      },
      {
        path: 'supplier-quotation/:token',
        loadComponent: () => import('@kiraah-shared/consulting/booking').then(m => m.SupplierQuotationPublicComponent)
      },
      {
        path: 'supplier-quote/:token/tracking',
        loadComponent: () => import('@kiraah-shared/sales').then(m => m.SupplierQuotationSubmissionPublicTrackingComponent)
      },
      {
        path: 'supplier-quote/:token',
        loadComponent: () => import('@kiraah-shared/sales').then(m => m.SupplierQuotationSubmissionPublicPortalComponent)
      },
      {
        path: 'booking',
        loadChildren: () => import('@kiraah-shared/consulting/booking').then(m => m.BookingPublicRoutingModule)
      },
      {
        path: 'survey/:token',
        loadComponent: () => import('@kiraah-shared/daily-activity/player').then(m => m.SurveyPublicPlayerComponent)
      },
      {
        path: 'learning-campaign/gateway/:token',
        loadComponent: () => import('@kiraah-shared/daily-activity/learning-campaign').then(m => m.LearningCampaignGatewayComponent)
      },
      {
        path: 'learning-campaign/public/:token',
        loadComponent: () => import('@kiraah-shared/daily-activity/learning-campaign').then(m => m.LearningCampaignPublicPlayerComponent)
      },
      {
        path: 'learning-campaign/public/:token/result',
        loadComponent: () => import('@kiraah-shared/daily-activity/learning-campaign').then(m => m.LearningCampaignPublicResultComponent)
      },
      {
        path: 'learning-campaign/public/:token/sequence/:sequenceUid/player/activity/:activityUid/items',
        loadComponent: () => import('@kiraah-shared/daily-activity').then(m => m.ActivityItemPlayerViewComponent)
      },
      {
        path: 'tenant-subscription/public/:token',
        loadComponent: () => import('@kiraah-shared/licensing').then(m => m.TenantSubscriptionPublicOnboardingComponent)
      },
      {
        path: 'sessions/subscription-onboarding',
        loadComponent: () => import('@kiraah-shared/licensing').then(m => m.TenantSubscriptionPublicOnboardingComponent)
      },
      {
        path: 'engagement/:slug',
        loadComponent: () => import('@kiraah-shared/academy').then(m => m.PublicEngagementPublicPlayerComponent)
      },
      {
        path: 'engagement/:slug/result',
        loadComponent: () => import('@kiraah-shared/academy').then(m => m.PublicEngagementAnonymousResultComponent)
      },
      {
        path: 'engagement/:slug/sequence/:sequenceUid/player/activity/:activityUid/items',
        loadComponent: () => import('@kiraah-shared/daily-activity').then(m => m.ActivityItemPlayerViewComponent)
      },
      {
        path: 'catalog',
        loadChildren: () => import('@kiraah-shared/academy').then(m => m.CatalogPublicRoutingModule)
      },
      {
        path: 'others',
        loadChildren: () => import('@kiraah-shared/app-skeleton').then(m => m.OthersRoutingModule)
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutSidebarLargeComponent,
    canActivate: [AuthGuard],
    children: [
      ...commonAdminRoutes,
    ]
  },
  {
    path: '**',
    redirectTo: 'others/404'
  }
];
